// Gameplay management module for PPT Hero game
export default class GameplayManager {
  constructor(scene) {
    this.scene = scene;
  }

  // Start gameplay
  startGameplay() {
    try {
      console.log('🎮 Starting gameplay');
      
      // Set game state
      this.scene.gameState = 'playing';
      this.scene.inputEnabled = true;
      
      // Create level shapes
      this.scene.createLevelShapes();
      
      // Start level timer
      this.scene.startLevelTimer();
      
      // Show objective
      this.showObjective();
      
      // Enable input handlers
      this.enableInputHandlers();
      
      console.log('✅ Gameplay started successfully');
      
    } catch (error) {
      console.error('Error starting gameplay:', error);
    }
  }

  // Show level objective
  showObjective() {
    try {
      const levelData = this.scene.levels[this.scene.currentLevel];
      if (!levelData || !levelData.instructions) return;
      
      // Create objective text
      this.scene.objectiveText = this.scene.add.text(
        this.scene.cameras.main.width / 2,
        150,
        levelData.instructions,
        this.scene.getProfessionalTextStyle("18px", "#ffffff", "500")
      ).setOrigin(0.5).setDepth(1000);
      
      // Auto-hide after 8 seconds
      this.scene.time.delayedCall(8000, () => {
        if (this.scene.objectiveText) {
          this.scene.objectiveText.destroy();
          this.scene.objectiveText = null;
        }
      });
      
    } catch (error) {
      console.error('Error showing objective:', error);
    }
  }

  // Enable input handlers
  enableInputHandlers() {
    try {
      // Set up keyboard input
      this.scene.input.keyboard.on('keydown', (event) => {
        this.scene.inputManager.handleKeyDown(event);
      });
      
      // Set up pointer events
      this.scene.input.on('pointerdown', (pointer, objectsUnderPointer) => {
        this.scene.inputManager.onPointerDown(pointer, objectsUnderPointer);
      });
      
      this.scene.input.on('pointermove', (pointer) => {
        this.scene.inputManager.onPointerMove(pointer);
      });
      
      this.scene.input.on('pointerup', () => {
        this.scene.inputManager.onPointerUp();
      });
      
      // Set up drag events
      this.scene.input.on('dragstart', (pointer, gameObject) => {
        this.scene.inputManager.onDragStart(pointer, gameObject);
      });
      
      this.scene.input.on('drag', (pointer, gameObject, dragX, dragY) => {
        this.scene.inputManager.onDrag(pointer, gameObject, dragX, dragY);
      });
      
      this.scene.input.on('dragend', (pointer, gameObject) => {
        this.scene.inputManager.onDragEnd(pointer, gameObject);
      });
      
    } catch (error) {
      console.error('Error enabling input handlers:', error);
    }
  }

  // Check level completion
  checkLevelCompletion() {
    try {
      if (this.scene.levelComplete || this.scene.isTransitioning) return;
      
      const levelData = this.scene.levels[this.scene.currentLevel];
      if (!levelData || !levelData.goal) return;
      
      // Check if goal is met
      if (levelData.goal(this.scene)) {
        this.completeLevel();
      }
      
    } catch (error) {
      console.error('Error checking level completion:', error);
    }
  }

  // Complete level
  completeLevel() {
    try {
      console.log('🎉 Level completed!');
      
      // Set completion state
      this.scene.levelComplete = true;
      this.scene.isTransitioning = true;
      
      // Stop timer
      this.scene.stopLevelTimer();
      
      // Calculate score
      const score = this.calculateLevelScore();
      this.scene.score += score;
      
      // Save analytics
      this.scene.saveLevelAnalytics(true);
      
      // Update progress
      const currentRound = this.scene.getCurrentRound();
      if (currentRound) {
        this.scene.updateRoundProgress(currentRound.id, this.scene.currentLevel, true, score);
      }
      
      // Show completion sequence
      this.showCompletionSequence(score);
      
    } catch (error) {
      console.error('Error completing level:', error);
    }
  }

  // Calculate level score
  calculateLevelScore() {
    try {
      let baseScore = 100;
      
      // Time bonus
      if (this.scene.timeRemaining > 0) {
        baseScore += Math.floor(this.scene.timeRemaining * 2);
      }
      
      // Shortcut usage bonus
      if (this.scene.analyticsData && this.scene.analyticsData.shortcutsUsed) {
        const shortcutsUsed = Object.keys(this.scene.analyticsData.shortcutsUsed).length;
        baseScore += shortcutsUsed * 10;
      }
      
      // Error penalty
      if (this.scene.analyticsData && this.scene.analyticsData.errors) {
        const totalErrors = this.scene.analyticsData.errors.misstypedShortcuts + 
                           this.scene.analyticsData.errors.incorrectSequences + 
                           this.scene.analyticsData.errors.missclicks;
        baseScore -= totalErrors * 5;
      }
      
      return Math.max(50, baseScore);
      
    } catch (error) {
      console.error('Error calculating level score:', error);
      return 100;
    }
  }

  // Show completion sequence
  showCompletionSequence(score) {
    try {
      const currentRound = this.scene.getCurrentRound();
      const roundName = currentRound ? currentRound.name : "Practice";
      
      // Calculate level number within round
      let levelNumberInRound = 1;
      if (currentRound && currentRound.levels) {
        const levelIndexInRound = currentRound.levels.indexOf(this.scene.currentLevel);
        levelNumberInRound = levelIndexInRound >= 0 ? levelIndexInRound + 1 : 1;
      }
      
      // Show success notification
      this.scene.showSuccessNotification(roundName, levelNumberInRound, score, 0);
      
      // Transition to next level after delay
      this.scene.time.delayedCall(3500, () => {
        this.transitionToNextLevel();
      });
      
    } catch (error) {
      console.error('Error showing completion sequence:', error);
    }
  }

  // Clean transition to next level
  transitionToNextLevel() {
    // If the scene got restarted/shutdown, do nothing
    if (!this.scene.scene.isActive()) return;
    
    // Set transitioning state
    this.scene.gameState = 'transitioning';
    
    // Clear shapes cleanly (they should already be faded)
    this.scene.clearAllShapes();
    
    // Reset top bar color
    if (this.scene.objectiveText) {
      this.scene.objectiveText.setStyle({ color: '#ffffff' });
    }
    
    // Progress to next level with smooth transition
    this.scene.nextLevelImmediate();
  }

  // Complete round
  completeRound(round) {
    try {
      console.log(`🎊 Round completed: ${round.name}`);
      
      // Show round completion message
      this.scene.showCenterNotification(`🎉 ${round.name} Round Complete!`, 'success', 3000);
      
      // Return to practice mode
      setTimeout(() => {
        this.scene.selectRound(0);
      }, 3000);
      
    } catch (error) {
      console.error('Error completing round:', error);
    }
  }

  // Pause gameplay
  pauseGameplay() {
    try {
      this.scene.gameState = 'paused';
      this.scene.inputEnabled = false;
      
      // Pause timer
      if (this.scene.timerEvent) {
        this.scene.timerEvent.paused = true;
      }
      
      this.scene.showCenterNotification('Game paused', 'warning', 2000);
      
    } catch (error) {
      console.error('Error pausing gameplay:', error);
    }
  }

  // Resume gameplay
  resumeGameplay() {
    try {
      this.scene.gameState = 'playing';
      this.scene.inputEnabled = true;
      
      // Resume timer
      if (this.scene.timerEvent) {
        this.scene.timerEvent.paused = false;
      }
      
      this.scene.showCenterNotification('Game resumed', 'success', 1500);
      
    } catch (error) {
      console.error('Error resuming gameplay:', error);
    }
  }

  // End gameplay
  endGameplay() {
    try {
      this.scene.gameState = 'ended';
      this.scene.inputEnabled = false;
      
      // Stop timer
      this.scene.stopLevelTimer();
      
      // Clear objective
      if (this.scene.objectiveText) {
        this.scene.objectiveText.destroy();
        this.scene.objectiveText = null;
      }
      
      console.log('🎮 Gameplay ended');
      
    } catch (error) {
      console.error('Error ending gameplay:', error);
    }
  }
}
